import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { doc, Firestore, onSnapshot } from "@angular/fire/firestore";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthStateService } from "@core/auth-state.service";
import { IonicModule } from "@ionic/angular";
import { TranslatePipe } from "@ngx-translate/core";
import { Observable, Subscription } from "rxjs";
import { switchMap, take } from "rxjs/operators";

@Component({
  selector: "app-api-loader",
  standalone: true,
  templateUrl: "./api-loader.component.html",
  styleUrl: "./api-loader.component.scss",
  imports: [IonicModule, TranslatePipe],
})
export class ApiLoaderComponent implements OnInit, OnDestroy {
  public showLoader = true;
  public isSuccess = false;
  public isFailure = false;
  public isTimeout = false;
  public userId: string;
  private asyncCallDocSubscription: Subscription | undefined;

  constructor(
    private matDialog: MatDialog,
    private authService: AuthService,
    private authStateService: AuthStateService,
    private firestore: Firestore,

    @Inject(MAT_DIALOG_DATA) public asyncCallDocId: string,
  ) { }

  async ngOnInit() {
    await this.authService
      ?.getUid("ApiLoaderComponent")
      ?.then((uid) => {
        this.userId = uid;
      })
      .catch((error) => {
        return error;
      });

    this.trackAsyncCallDoc(this.asyncCallDocId);
  }

  private trackAsyncCallDoc(docId: string): void {
    const userId = this.userId;
    if (!userId) {
      return;
    }

    const docRef = doc(this.firestore, `users/${userId}/async_module_call/${docId}`);

    const path = `users/${userId}/async_module_call/${docId}`;
    console.info("[trackAsyncCallDoc] Creating Firestore docRef for path:", path);

    // ✅ FIX: Gate the onSnapshot listener on customTokenReady$ so it never
    // opens before the Firebase custom token is confirmed (LOTTOSOCIAL-PWA-1MED).
    const asyncCallObservable = this.authStateService.customTokenReady$.pipe(
      take(1),
      switchMap(() => new Observable<Data>((observer) => {
        const unsubscribe = onSnapshot(docRef, (docSnapshot) => {
          if (docSnapshot.exists()) {
            observer.next(docSnapshot.data() as Data);
          } else {
            observer.error(new Error('Document does not exist'));
          }
        }, (error) => {
          observer.error(error);
        });

        // Cleanup logic
        return () => unsubscribe();
      }))
    );

    // Subscribing to the Observable
    this.asyncCallDocSubscription = asyncCallObservable.subscribe(
      (idData: Data) => {
        const outcomeMessage: string = idData?.user_outcome_message?.status;
        if (outcomeMessage && outcomeMessage !== "") {
          if (outcomeMessage.toLowerCase().includes("success")) {
            this.isSuccess = true;
          } else {
            this.isFailure = true;
            const errorMessage = idData?.user_outcome_message?.error_reason;
            if (errorMessage) {
              this.showErrorToast(errorMessage);
            }
          }
          this.showLoader = false;
          this.asyncCallDocSubscription?.unsubscribe();
        }
        if (idData?.timeout_msg) {
          this.isTimeout = true;
          this.showLoader = false;
          this.asyncCallDocSubscription?.unsubscribe();
        }
      },
      (error) => {
        this.isFailure = true;
        this.showLoader = false;
        this.asyncCallDocSubscription?.unsubscribe();
      }
    );
  }
  closeDialog() {
    if (this.asyncCallDocSubscription) {
      this.asyncCallDocSubscription.unsubscribe();
    }
    this.matDialog.closeAll();
  }

  ngOnDestroy() {
    if (this.asyncCallDocSubscription) {
      this.asyncCallDocSubscription.unsubscribe();
    }
  }

  private showErrorToast(message = ""): void {
    const defaultMsg = {
      message: {
        line_1: message,
        line_2: "",
      },
      style: {
        type: "Error",
        icon: "tick circle error.svg",
      },
    };
    this.authService.showToastMessage({}, defaultMsg);
  }
}
